import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Home } from 'lucide-react';
import * as api from '../../api/client';
import { useInstagramAccounts } from '../../context/InstagramAccountContext';
import PageHeader from '../components/PageHeader';
import NoAccountState from '../components/NoAccountState';

type Period = 7 | 30;

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { activeAccount } = useInstagramAccounts();
  const [period, setPeriod] = useState<Period>(30);
  const [summary, setSummary] = useState<api.AccountMetricsSummary | null>(null);
  const [posts, setPosts] = useState<api.ScheduledPost[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!activeAccount) {
      setSummary(null);
      setPosts([]);
      return;
    }
    setLoading(true);
    setSummary(null);
    setPosts([]);
    try {
      const [metrics, scheduled] = await Promise.all([
        api.getAccountMetricsSummary(activeAccount.id),
        api.listScheduledPosts(activeAccount.id),
      ]);
      setSummary(metrics);
      setPosts(scheduled);
    } finally {
      setLoading(false);
    }
  }, [activeAccount]);

  useEffect(() => {
    load();
  }, [load]);

  const accountLabel = activeAccount?.igUsername
    ? `@${activeAccount.igUsername}`
    : activeAccount?.pageName || '';

  const since = useMemo(() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - period);
    d.setUTCHours(0, 0, 0, 0);
    return d;
  }, [period]);

  const publishedInPeriod = useMemo(
    () =>
      posts.filter((p) => {
        if (p.status !== 'published') return false;
        const at = p.publishedAt || p.scheduledAt;
        return at ? new Date(at) >= since : false;
      }),
    [posts, since]
  );

  const engagementByPost = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of summary?.topPosts ?? []) {
      map.set(p.scheduledPostId, p.likes + p.comments);
    }
    return map;
  }, [summary]);

  const totalEngagement = useMemo(() => {
    if (!summary) return 0;
    return summary.totalLikes + summary.totalComments;
  }, [summary]);

  const avgEngagement = useMemo(() => {
    const n = publishedInPeriod.length || (summary?.topPosts.length ?? 0);
    if (!n || !totalEngagement) return 0;
    // Prefer period post count when available; otherwise fall back to top-post average.
    if (publishedInPeriod.length > 0) {
      const known = publishedInPeriod.reduce((sum, p) => sum + (engagementByPost.get(p.id) ?? 0), 0);
      const knownCount = publishedInPeriod.filter((p) => engagementByPost.has(p.id)).length;
      if (knownCount > 0) return Math.round(known / knownCount);
      return Math.round(totalEngagement / publishedInPeriod.length);
    }
    return Math.round(totalEngagement / n);
  }, [publishedInPeriod, engagementByPost, totalEngagement, summary]);

  const weeklyBars = useMemo(() => {
    const weeks = 4;
    const now = new Date();
    const buckets: { label: string; value: number }[] = [];
    for (let i = weeks - 1; i >= 0; i--) {
      const end = new Date(now);
      end.setUTCDate(end.getUTCDate() - i * 7);
      end.setUTCHours(23, 59, 59, 999);
      const start = new Date(end);
      start.setUTCDate(start.getUTCDate() - 6);
      start.setUTCHours(0, 0, 0, 0);

      const value = posts
        .filter((p) => {
          if (p.status !== 'published') return false;
          const at = p.publishedAt || p.scheduledAt;
          if (!at) return false;
          const d = new Date(at);
          return d >= start && d <= end;
        })
        .reduce((sum, p) => sum + (engagementByPost.get(p.id) ?? 1), 0);

      buckets.push({
        label: t('dashboard.weekLabel', { n: weeks - i }),
        value,
      });
    }
    return buckets;
  }, [posts, engagementByPost, t]);

  const maxBar = Math.max(...weeklyBars.map((b) => b.value), 1);

  const topPosts = summary?.topPosts.slice(0, 3) ?? [];

  if (!activeAccount) {
    return <NoAccountState />;
  }

  return (
    <div>
      <PageHeader
        icon={Home}
        title={t('dashboard.title', { account: accountLabel })}
        subtitle={t('dashboard.subtitle')}
        action={
          <div className="flex gap-3">
            {([7, 30] as const).map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setPeriod(days)}
                className={`px-4 py-2 rounded-md text-[13px] font-semibold transition-colors ${
                  period === days
                    ? 'bg-lc-pink text-white dark:text-lc-ink border border-lc-pink'
                    : 'bg-transparent border border-[#e8ddd0] dark:border-lc-borderStrong text-[#241c14] dark:text-lc-text hover:border-lc-pink/40'
                }`}
              >
                {t('dashboard.periodDays', { days })}
              </button>
            ))}
          </div>
        }
      />

      {loading || !summary ? (
        <p className="text-[#7a6b5d] dark:text-lc-faint text-sm">{t('common.loading')}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              label={t('dashboard.postsThisPeriod')}
              value={publishedInPeriod.length.toLocaleString(i18n.language)}
              hint={t('dashboard.postsHint', { days: period })}
              color="text-lc-pink"
            />
            <StatCard
              label={t('dashboard.totalEngagement')}
              value={totalEngagement.toLocaleString(i18n.language)}
              hint={t('dashboard.engagementHint')}
              color="text-lc-purple"
            />
            <StatCard
              label={t('dashboard.avgEngagement')}
              value={avgEngagement.toLocaleString(i18n.language)}
              hint={t('dashboard.avgHint')}
              color="text-lc-orange"
            />
            <StatCard
              label={t('dashboard.bestHour')}
              value={api.formatBestHour(summary)}
              hint={
                summary.isDefaultBestHour
                  ? t('dashboard.bestHourDefaultHint')
                  : t('dashboard.bestHourDataHint')
              }
              color="text-[#34e2d1]"
              badge={summary.isDefaultBestHour ? t('dashboard.bestHourDefaultBadge') : undefined}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
            <div className="card p-6">
              <h3 className="text-base font-bold text-[#241c14] dark:text-lc-text mb-5">
                {t('dashboard.weeklyTitle')}
              </h3>
              <div className="flex items-end justify-around h-[200px] pb-4 gap-3">
                {weeklyBars.map((bar, i) => {
                  const height = Math.max(8, Math.round((bar.value / maxBar) * 180));
                  const isLast = i === weeklyBars.length - 1;
                  return (
                    <div key={bar.label} className="flex flex-col items-center gap-2 flex-1 max-w-[48px]">
                      <div
                        className="w-8 rounded"
                        style={{
                          height,
                          background: isLast
                            ? 'linear-gradient(to top, #b892ff, #b892ff44)'
                            : 'linear-gradient(to top, #ff6f91, #ff6f9144)',
                        }}
                        title={String(bar.value)}
                      />
                      <div className="text-[11px] text-[#7a6b5d] dark:text-lc-faint whitespace-nowrap">
                        {bar.label}
                      </div>
                    </div>
                  );
                })}
              </div>
              {weeklyBars.every((b) => b.value === 0) && (
                <p className="text-center text-xs text-[#7a6b5d] dark:text-lc-faint -mt-2">
                  {t('dashboard.noWeeklyData')}
                </p>
              )}
            </div>

            <div className="card p-6">
              <h3 className="text-base font-bold text-[#241c14] dark:text-lc-text mb-5">
                {t('dashboard.topPostsTitle')}
              </h3>
              {topPosts.length === 0 ? (
                <p className="text-sm text-[#7a6b5d] dark:text-lc-faint">{t('dashboard.noData')}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {topPosts.map((post) => {
                    const engagement = post.likes + post.comments;
                    const rate =
                      totalEngagement > 0
                        ? Math.round((engagement / totalEngagement) * 100)
                        : 0;
                    return (
                      <div
                        key={post.scheduledPostId}
                        className="p-3 rounded-lg bg-white dark:bg-lc-bg border border-[#e8ddd0] dark:border-lc-border flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-[#241c14] dark:text-lc-text mb-1 truncate">
                            {t('dashboard.postLabel', { id: post.scheduledPostId.slice(0, 8) })}
                          </div>
                          <div className="text-[11px] text-[#7a6b5d] dark:text-lc-faint">
                            {t('dashboard.engagementCount', {
                              count: engagement,
                            })}
                          </div>
                        </div>
                        <div className="text-[13px] font-bold text-lc-pink shrink-0">{rate}%</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  color,
  badge,
}: {
  label: string;
  value: string;
  hint: string;
  color: string;
  badge?: string;
}) {
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#7a6b5d] dark:text-lc-faint">
          {label}
        </div>
        {badge ? (
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#34e2d1]/15 text-[#1a9e92] dark:text-[#34e2d1]">
            {badge}
          </span>
        ) : null}
      </div>
      <div className={`text-[28px] font-extrabold mb-1 ${color}`}>{value}</div>
      <div className="text-xs text-[#7a6b5d] dark:text-lc-muted">{hint}</div>
    </div>
  );
}
